import {useEffect, useState} from 'react'
import _ from 'lodash'
import {IMixerChannel} from '../channel-collection'
import {
  ICutStates,
  ISortScores,
  SemanticFilteringChannelKeys,
  SemanticFilteringItemType,
  useChannelComponents,
} from '../..'
import './style.css'

export interface ISemanticScoreComponentsProps {
  data: SemanticFilteringItemType
  channels?: Record<SemanticFilteringChannelKeys, IMixerChannel[]>
  sortEpsilon?: number
}

export function SemanticScoreComponents(props: ISemanticScoreComponentsProps) {
  const {data, sortEpsilon, channels} = {
    sortEpsilon: 0.001,
    ...props,
  }

  const {calculateSortScores, calculateCuts: calculateAdjustCuts} =
    useChannelComponents<SemanticFilteringItemType>(channels?.adjust)

  const {calculateCuts: calculateLimitCuts} = useChannelComponents<SemanticFilteringItemType>(
    channels?.limit
  )

  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false)
  const [showCutBreakdown, setShowCutBreakdown] = useState(false)
  const [showLimitBreakdown, setShowLimitBreakdown] = useState(false)

  const [scores, setScores] = useState<{
    sort: ISortScores['scores']
    cut: ICutStates['inRange']
    limit: ICutStates['inRange']
  }>({
    sort: {},
    cut: {},
    limit: {},
  })
  const [meanScore, setMeanScore] = useState<number>(0)

  useEffect(() => {
    if (!data) return
    if (!channels) return
    const {scores, mean} = calculateSortScores(data)
    const {inRange: adjustCutStates} = calculateAdjustCuts(data)
    const {inRange: limitCutStates} = calculateLimitCuts({
      ...data,
      sortScore: mean,
    })
    setScores({
      sort: scores,
      cut: adjustCutStates,
      limit: limitCutStates,
    })
    setMeanScore(mean)
  }, [data, channels])

  return (
    <div className='score-components'>
      {!data || !channels || _.isNaN(meanScore) ? null : (
        <div
          className='component sort'
          style={{
            backgroundColor:
              Math.abs(meanScore) < sortEpsilon ?
                'rgba(150, 50, 50, 0.5)'
              : 'rgba(50, 150, 50, 0.5)',
          }}
        >
          <div className='title'>sort</div>
          <div className='content'>
            {showScoreBreakdown ?
              <ul>
                {_.chain(scores.sort)
                  .toPairs()
                  .orderBy(([_k, v]) => v, ['desc'])
                  .map(([k, v]) => (
                    <li
                      key={k}
                      style={!v ? {opacity: 0.5} : undefined}
                    >
                      {k}: {_.isNumber(v) ? v.toFixed(3) : v}
                    </li>
                  ))
                  .value()}
              </ul>
            : null}
            <div className='sum'>
              {showScoreBreakdown ?
                <div
                  className='link'
                  onClick={() => setShowScoreBreakdown(false)}
                >
                  - hide breakdown
                </div>
              : <div
                  className='link'
                  onClick={() => setShowScoreBreakdown(true)}
                >
                  + show breakdown
                </div>
              }
              <span>
                <b>MEAN:</b>&nbsp;
                {_.isNumber(meanScore) ? meanScore.toFixed(3) : meanScore}
              </span>
            </div>
          </div>
        </div>
      )}

      {_.keys(scores.cut).length === 0 ? null : (
        <div
          className='component cut'
          style={{
            backgroundColor:
              _.values(scores.cut).includes(false) ?
                'rgba(150, 50, 50, 0.5)'
              : 'rgba(50, 150, 50, 0.5)',
          }}
        >
          <div className='title'>cut</div>
          <div className='content'>
            {showCutBreakdown ?
              <ul>
                {_.chain(scores.cut)
                  .toPairs()
                  .orderBy(([k]) => k)
                  .map(([k, v]) => (
                    <li key={k}>
                      {k}: {v ? 'include' : 'exclude'}
                    </li>
                  ))
                  .value()}
              </ul>
            : null}
            <div className='sum'>
              {showCutBreakdown ?
                <div
                  className='link'
                  onClick={() => setShowCutBreakdown(false)}
                >
                  - hide breakdown
                </div>
              : <div
                  className='link'
                  onClick={() => setShowCutBreakdown(true)}
                >
                  + show breakdown
                </div>
              }
              <span>{_.values(scores.cut).includes(false) ? 'excluded' : 'included'}</span>
            </div>
          </div>
        </div>
      )}

      {_.keys(scores.limit).length === 0 ? null : (
        <div
          className='component limit'
          style={{
            backgroundColor:
              _.values(scores.limit).includes(false) ?
                'rgba(150, 50, 50, 0.5)'
              : 'rgba(50, 150, 50, 0.5)',
          }}
        >
          <div className='title'>limit</div>
          <div className='content'>
            {showLimitBreakdown ?
              <ul>
                {_.chain(scores.limit)
                  .toPairs()
                  .orderBy(([k]) => k)
                  .map(([k, v]) => (
                    <li key={k}>
                      {k}: {v ? 'include' : 'exclude'}
                    </li>
                  ))
                  .value()}
              </ul>
            : null}
            <div className='sum'>
              {showLimitBreakdown ?
                <div
                  className='link'
                  onClick={() => setShowLimitBreakdown(false)}
                >
                  - hide breakdown
                </div>
              : <div
                  className='link'
                  onClick={() => setShowLimitBreakdown(true)}
                >
                  + show breakdown
                </div>
              }
              <span>{_.values(scores.limit).includes(false) ? 'excluded' : 'included'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
